<template>
  <div
    v-if="containerVisible"
    class="timeMessage"
    role="status"
    :class="isStore && responsive === 'tablet' ? 'top' : 'bottom'">

    <!-- 임박 구간: onWarn() 트리거 이후 + 아직 만료 전 -->
    <div v-if="showClosingSoon" class="timeLeft">
      <p>{{ expiredMessage(minutesLeft) }}</p>
    </div>

    <!-- 만료 -->
    <p v-else-if="showExpired" class="timeExpired">{{ locale.over_order_time }}</p>
  </div>
</template>

<script>
import { nowMs } from '@/common/responseOffset'

export default {
  name: 'OrderTimer',
  props: {
    locale: { type: Object, default: () => ({}) },
    isStore: { type: Boolean, default: false },
    responsive: { type: String, default: 'mobile' },
    shopPageId: { type: String, default: '' }
  },
  computed: {
    orderLimitSetting () {
      return this.$store.getters['shop/getShopOrderLimit'](this.shopPageId) || {}
    },
    getBillingStartTime () {
      const shopPageId = this.shopPageId
      const shopCatCode = this.$store.getters['shop/getShopCatCode'](shopPageId)
      const startTime = this.$store.getters['shop/getBillingStartTime'](shopCatCode)
      return startTime
    },
    showClosingSoon () {
      return this.started && this.warned && this.left > 0
    },
    showExpired () {
      return this.started && this.left === 0
    },
    containerVisible () {
      return this.started && (this.showClosingSoon || this.showExpired)
    },
    minutesLeft () {
      if (!this.started || this.left <= 0) return 0
      return Math.max(1, Math.ceil(this.left / 60000))
    },
    secondsLeftWithinMinute () {
      if (!this.started || this.left <= 0) return 0
      return Math.floor(this.left / 1000)
    }
  },
  created () {
    // init / re-init watcher
    this._stopInitWatch = this.$watch(
      () => ({
        setting: this.orderLimitSetting,
        startTime: this.getBillingStartTime
      }),
      ({ startTime }) => {
        if (!this.hasValidSetting() || !startTime) return

        // startTime이 바뀌면 startMs도 바뀌어야 하므로 재초기화
        if (this._inited && startTime !== this._lastStartTime) {
          this._lastStartTime = startTime
          this.initTimers()
          return
        }

        if (!this._inited) {
          this._lastStartTime = startTime
          this.initTimers()
          return
        }

        // startTime은 그대로고, setting만 바뀐 케이스
        this.rescheduleByNewSetting()
      },
      { immediate: true }
    )

    document.addEventListener('visibilitychange', this.onWake)
    window.addEventListener('pageshow', this.onWake)
  },
  beforeDestroy () {
    this.teardown()
  },
  watch: {
    showExpired (isExpired) {
      this.$emit('orderTimeIsExpired', isExpired)

      // dispatch 중복 방지
      if (this._lastTimeoutState === isExpired) return
      this._lastTimeoutState = isExpired

      this.$store.dispatch('shop/setShopOrderTimeout', {
        shopPageId: this.shopPageId,
        isTimeout: isExpired
      })
    }
  },
  data () {
    return {
      _lastStartTime: null,
      _lastTimeoutState: null,

      startMs: null,     // 시작 시각(ms)
      endTime: null,     // 만료 시각(ms)
      left: -1,          // 현재 남은 시간(ms)
      warned: false,     // 임박 알림 1회 트리거 여부
      started: false,    // 시작 여부
      tickId: null,      // setInterval id (1초)
      preStartTimer: null, // 시작 대기타이머
      _inited: false     // 중복 초기화 방지
    }
  },
  methods: {
    hasValidSetting () {
      const m = Number(this.orderLimitSetting.orderableMessage)
      const t = Number(this.orderLimitSetting.orderableTime)
      return Number.isFinite(t) && t > 0 && Number.isFinite(m) && m >= 0
    },

    initTimers () {
      // 항상 리셋부터 (중복 타이머/대기타이머 방지)
      this.clearTimers()
      this.warned = false
      this.started = false

      const MINUTE = 60 * 1000
      const startTime = this.getBillingStartTime
      const startMs = startTime ? new Date(startTime).getTime() : NaN
      const durationMin = Number(this.orderLimitSetting.orderableTime) || 0

      if (!Number.isFinite(startMs) || durationMin <= 0) {
        this.left = -1
        this._inited = false
        return
      }

      this.startMs = startMs
      this.endTime = startMs + durationMin * MINUTE

      const now = nowMs()

      // 시작 전이면 start 시점에 맞춰 대기타이머 세팅
      if (now < this.startMs) {
        this.left = Math.max(0, this.endTime - now)
        this.preStartTimer = setTimeout(() => {
          this.startCountdown()
        }, Math.max(0, this.startMs - now))
        this._inited = true
        return
      }

      this.startCountdown()
      this._inited = true
    },

    startCountdown () {
      this.started = true
      this.warned = false
      this.clearTimers() // 남아있는 타이머 정리

      // 즉시 한 번 계산 후, 1초마다 갱신
      this.updateLeft()
      this.tickId = setInterval(() => {
        this.updateLeft()
      }, 1000)
    },

    rescheduleByNewSetting () {
      if (!Number.isFinite(this.startMs)) return

      const MINUTE = 60 * 1000
      const durationMin = Number(this.orderLimitSetting.orderableTime) || 0

      // 설정이 잠깐 0/undefined로 내려오는 케이스 대비: 만료로 굳히지 말고 중지
      if (durationMin <= 0) {
        this.started = false
        this.left = -1
        this.warned = false
        this.clearTimers()
        return
      }

      this.endTime = this.startMs + durationMin * MINUTE
      this.warned = false
      this.updateLeft()
    },

    updateLeft () {
      if (!this.endTime) return

      const now = nowMs()
      const prevLeft = this.left

      this.left = Math.max(0, this.endTime - now)

      const MINUTE = 60 * 1000
      const kMin = Math.max(0, Number(this.orderLimitSetting.orderableMessage) || 0)
      const warnMs = kMin * MINUTE

      if (!this.warned && warnMs > 0 && this.left > 0 && this.left <= warnMs) {
        this.onWarn()
      }

      // 만료 처리: UI는 0으로 보여주되, interval은 끊지 않음
      // (offset 갱신 후 정상 복귀 가능하게)
      if (this.left <= 0) {
        this.onExpire()
      }

      // 안전: NaN/이상치로 left가 -1로 떨어지면 복구
      if (prevLeft === 0 && this.left > 0) {
        // showExpired watcher가 false로 돌아가며 store도 풀림
      }
    },

    onWarn () {
      if (this.warned) return
      this.warned = true
    },

    onExpire () {
      if (!this.started) return
      this.left = 0
      // clearTimers() 하지 않음: 서버 offset 갱신 후 자동 복구 가능
    },

    onWake (e) {
      // pageshow는 bfcache 복원일 때만 의미 있게 처리(선택)
      if (e && e.type === 'pageshow' && e.persisted !== true) return

      if (!this.hasValidSetting() || !this.getBillingStartTime) return

      // wake 시점엔 그냥 재계산
      this.initTimers()
    },

    clearTimers () {
      if (this.tickId) {
        clearInterval(this.tickId)
        this.tickId = null
      }
      if (this.preStartTimer) {
        clearTimeout(this.preStartTimer)
        this.preStartTimer = null
      }
    },

    teardown () {
      this.clearTimers()
      document.removeEventListener('visibilitychange', this.onWake)
      window.removeEventListener('pageshow', this.onWake)
      if (this._stopInitWatch) {
        this._stopInitWatch()
        this._stopInitWatch = null
      }
    },

    expiredMessage (minute) {
      const second = this.secondsLeftWithinMinute
      return minute > 1
        ? this.locale.order_will_close.replace('{var}', minute)
        : this.locale.order_will_close_second.replace('{var}', second)
    }
  }
}
</script>

<style lang="scss" scoped>
.timeMessage {
  position: fixed;
  top: 6px;
  right: 0;
  font-size: 20px;
  font-weight: bold;
  text-shadow: 0 0 2px rgba(0,0,0,0.5);
  color: $color-white;
  border: 1px solid rgba(0,0,0,0.1);
  border-right: none;
  border-radius: 50px 0 0 50px;
  box-shadow: 0 0 8px rgba(0,0,0,0.2);
  overflow: hidden;
  z-index: 9;

  &.bottom {
    top: auto;
    bottom: 82px;
    right: 30px;
    text-align: center;
    border-right: 1px solid rgba(0,0,0,0.1);
    border-radius: 20px;
    box-shadow: 0 0 10px rgba(0,0,0,0.5);

    .timeLeft {
      flex-direction: column;
      max-width: 160px;
      padding: 12px;

      &:before {
        width: 24px; height: 24px;
        margin-right: 0; margin-bottom: 10px;
      }
    }
    .timeExpired { padding: 20px; }
  }
}

.timeLeft {
  display: flex; align-items: center;
  padding: 8px 32px 8px 16px;

  background: linear-gradient(90deg,#ff74c6 0%,#8b5cf6 25%,#60a5fa 50%,#8b5cf6 75%,#ff74c6 100%);
  background-size: 600% 100%;
  background-repeat: repeat;
  animation: gradient-loop 30s linear infinite;
  will-change: background-position;

  &:before {
    content: "";
    width: 20px; height: 20px; margin-right: 10px;
    background: url('~@/assets/img/ico_clock_wh.png') 50% 50% / contain no-repeat;
    opacity: 0.7;
  }
}

.timeExpired {
  padding: 8px 32px;
  background-color: rgba(#de1313, 0.8);
}

@keyframes gradient-loop{
  from { background-position: 0% 50%; }
  to   { background-position: 600% 50%; }
}

/* 모션 민감 사용자 */
@media (prefers-reduced-motion: reduce){
  .timeLeft{ animation: none; }
}

@media screen and (max-width: 767px) {
  .timeMessage {
    &.bottom {
      left: auto;
      right: 8px;
    }
  }
}
</style>